import type { ManualDoc } from "../types.ts"
import { t } from "./t.ts"

export const SHIPPER_DOCS: ManualDoc[] = [
  {
    slug: "intake",
    category: "shipper",
    order: 1,
    title: t("반품 접수 등록", "返品の受付登録"),
    summary: t(
      "고객이 보내는 반품을 시스템에 먼저 올립니다. 여기서부터 한 건이 시작됩니다.",
      "お客様が送る返品を先にシステムへ登録します。ここから1件が始まります。",
    ),
    roles: ["shipper", "admin"],
    keywords: ["접수", "등록", "신규", "반품사유", "반품수량", "受付", "登録", "返品理由"],
    body: [
      {
        kind: "p",
        text: t(
          "고객이 반품을 보내겠다고 알려 오면, 상자가 일본에 닿기 전에 먼저 등록합니다. 쿨트랙JP는 이 등록을 보고 상자를 기다리고, 도착하면 여기 적은 수량과 맞춰 봅니다.",
          "お客様から返品の連絡が来たら、箱が日本に届く前に先に登録します。日本側はこの登録を見て箱を待ち、到着したらここに書いた数量と突き合わせます。",
        ),
      },
      { kind: "heading", text: t("등록하기", "登録の手順") },
      {
        kind: "steps",
        items: [
          t("왼쪽 메뉴에서 반품 접수를 엽니다.", "左メニューから返品受付を開きます。"),
          t(
            "접수 정보를 채웁니다 — 접수일, 주문번호, 고객명, 연락처, 반품사유.",
            "受付情報を入力します — 受付日、注文番号、顧客名、連絡先、返品理由。",
          ),
          t(
            "반품 상품에 무엇이 몇 개 돌아오는지 적습니다. 품목 마스터에서 고르거나 직접 입력을 골라 상품명을 칩니다. 줄 추가로 여러 상품을 담을 수 있습니다.",
            "返品商品に何が何点戻るのかを記入します。品目マスタから選ぶか、直接入力を選んで商品名を入力します。行を追加で複数の商品を登録できます。",
          ),
          t(
            "입고 정보에 반입택배사와 반송장번호를 적습니다. 아직 모르면 비워 둡니다.",
            "入庫情報に配送業者と返送伝票番号を記入します。まだ分からなければ空のままにします。",
          ),
          t("접수 등록을 누릅니다.", "受付登録を押します。"),
        ],
      },      {
        kind: "image",
        src: t("/help/intake-ko.png", "/help/intake-ja.png"),
        alt: t("반품 접수 화면", "返品受付の画面"),
        caption: t(
          "접수 정보와 반품 상품을 채운 모습. 반품 상품은 줄 추가로 여러 개를 담을 수 있습니다.",
          "受付情報と返品商品を入力した状態。返品商品は行を追加で複数登録できます。",
        ),
      },

      { kind: "heading", text: t("무엇을 적는 칸인가", "各項目について") },
      {
        kind: "table",
        head: [t("칸", "項目"), t("설명", "説明"), t("필수", "必須")],
        rows: [
          [t("접수일", "受付日"), t("고객의 반품 요청을 받은 날. 오늘 날짜가 미리 들어 있습니다.", "お客様から返品の連絡を受けた日。本日の日付が入っています。"), t("필수", "必須")],
          [t("주문번호", "注文番号"), t("원래 주문의 번호. 인보이스에 그대로 실립니다.", "元の注文番号。インボイスにそのまま記載されます。"), t("필수", "必須")],
          [t("고객명", "顧客名"), t("반품을 보내는 사람. 인보이스에 실립니다.", "返品を送る方のお名前。インボイスに記載されます。"), t("필수", "必須")],
          [t("연락처", "連絡先"), t("일본 전화번호 표기로 자동 정리됩니다.", "日本の電話番号表記に自動整形されます。"), t("선택", "任意")],
          [t("반품사유", "返品理由"), t("단순 변심·불량·오배송·파손·주소불명·기타 중에서 고릅니다.", "自己都合・不良・誤配送・破損・住所不明・その他から選びます。"), t("필수", "必須")],
          [t("반품 상품 / 반품수량", "返品商品 / 返品数量"), t("한 줄 이상 적어야 합니다. 쿨트랙JP가 이 수량과 실물을 맞춰 봅니다.", "1行以上必要です。日本側がこの数量と現物を突き合わせます。"), t("필수", "必須")],
          [t("반입택배사", "配送業者"), t("사가와 또는 야마토.", "佐川またはヤマト。"), t("선택", "任意")],
          [t("반송장번호", "返送伝票番号"), t("숫자 12자리. 모르면 비워 둡니다.", "数字12桁。分からなければ空欄にします。"), t("선택", "任意")],
          [t("비고", "備考"), t("전달할 말. 입고된 뒤에도 고칠 수 있는 몇 안 되는 칸입니다.", "申し送り事項。入庫後も修正できる数少ない欄です。"), t("선택", "任意")],
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "관리자 계정으로 등록할 때는 화주를 고르는 칸이 하나 더 있습니다. 화주 계정은 자기 화주로 자동 저장됩니다.",
          "管理者アカウントで登録する場合は荷主を選ぶ欄が1つ増えます。荷主のアカウントは自分の荷主で自動保存されます。",
        ),
      },
      { kind: "heading", text: t("등록한 뒤", "登録後") },
      {
        kind: "list",
        items: [
          t("상태가 접수완료가 되고, 바로 그 건의 반품 상세 화면으로 갑니다.", "状態が受付完了になり、その件の返品詳細画面に移動します。"),
          t(
            "상자가 오지 않은 채 14일이 지나면 쿨트랙JP의 할 일에 미입고 경고로 뜹니다.",
            "箱が届かないまま14日が過ぎると、日本側の対応待ちに未入庫アラートとして表示されます。",
          ),
          t(
            "잘못 등록했다면 입고 전까지는 반품 상세에서 접수 취소를 누를 수 있습니다.",
            "誤って登録した場合、入庫前であれば返品詳細から受付取消ができます。",
          ),
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "입고된 뒤에는 주문번호·고객명·반품 상품을 고칠 수 없습니다. 그 값으로 이미 인보이스가 만들어지기 때문입니다. 연락처와 비고만 고칠 수 있습니다.",
          "入庫後は注文番号・顧客名・返品商品を修正できません。その値でインボイスが作られるためです。修正できるのは連絡先と備考だけです。",
        ),
      },
    ],
  },

  {
    slug: "tracking",
    category: "shipper",
    order: 2,
    title: t("반송장번호 관리", "返送伝票番号の管理"),
    summary: t(
      "상자를 찾아내는 유일한 열쇠입니다. 적는 규칙과 나중에 채우는 방법.",
      "箱を見つけるための唯一の手がかりです。記入ルールと後から入力する方法。",
    ),
    keywords: ["반송장", "운송장", "송장", "번호", "사가와", "야마토", "伝票番号", "追跡番号"],
    body: [
      {
        kind: "p",
        text: t(
          "쿨트랙JP는 상자에 붙은 번호를 찍어 어느 반품 건인지 찾습니다. 그래서 이 번호가 시스템과 실물을 잇는 유일한 열쇠입니다.",
          "日本側は箱に貼られた番号を読み取って、どの返品かを特定します。この番号がシステムと現物をつなぐ唯一の手がかりです。",
        ),
      },
      {
        kind: "list",
        items: [
          t("숫자 12자리입니다(사가와·야마토 모두).", "数字12桁です(佐川・ヤマトとも)。"),
          t(
            "하이픈이나 공백을 넣어 붙여 넣어도 알아서 숫자만 남깁니다. 화면에는 1234-5678-9012 모양으로 보입니다.",
            "ハイフンや空白を含めて貼り付けても数字だけが残ります。画面では 1234-5678-9012 の形で表示されます。",
          ),
          t(
            "같은 화주 안에서 번호는 하나뿐입니다. 이미 있는 번호를 또 적으면 이미 등록된 반송장번호입니다 라고 막습니다.",
            "同じ荷主の中で番号は1つだけです。既にある番号を入力すると「すでに登録された返送伝票番号です」と表示されます。",
          ),
        ],
      },
      { kind: "heading", text: t("아직 번호를 모를 때", "番号がまだ分からないとき") },
      {
        kind: "p",
        text: t(
          "비워 두고 접수해도 됩니다. 다만 번호가 없으면 스캔으로 찾을 수 없으므로, 알게 되면 다음 두 가지 중 하나로 채웁니다.",
          "空欄のまま受付できます。ただし番号がないとスキャンで見つけられないため、分かった時点で次のいずれかで入力します。",
        ),
      },
      {
        kind: "list",
        items: [
          t(
            "아직 접수완료 상태라면 — 화주가 반품 상세에서 접수 내용을 고쳐 채웁니다.",
            "まだ受付完了の状態なら — 荷主が返品詳細で受付内容を修正して入力します。",
          ),
          t(
            "상자가 이미 일본에 도착했다면 — 쿨트랙JP가 반품 상세에서 손으로 입고 처리를 하며 상자에 적힌 번호를 함께 채웁니다.",
            "箱がすでに日本に届いていれば — 日本側が返品詳細で「手入力で入庫」を行い、箱に書かれた番号を一緒に入力します。",
          ),
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "이미 번호가 적힌 건에 다른 번호로 손 입고를 하면 이 건에 적힌 반송장번호와 다릅니다 라고 막습니다. 다른 상자를 찍은 것일 수 있으니 번호를 다시 확인하세요.",
          "すでに番号が入っている件に別の番号で手入力入庫をすると「この件の返送伝票番号と異なります」と表示されます。別の箱の可能性があるので番号を確認してください。",
        ),
      },
      {
        kind: "p",
        text: t(
          "검색할 때는 하이픈을 넣든 빼든 같은 번호로 찾습니다. 반품 목록의 검색칸과 위쪽 통합 검색 모두 그렇습니다.",
          "検索時はハイフンの有無にかかわらず同じ番号として検索できます。返品一覧の検索欄でも上部の統合検索でも同じです。",
        ),
      },
    ],
  },

  {
    slug: "arrival-check",
    category: "shipper",
    order: 3,
    title: t("입고 확인과 처리방안", "入庫確認と処理方針"),
    summary: t(
      "상자가 일본에 들어온 뒤, 화주가 내용을 확인하고 한국반송인지 폐기인지 정합니다.",
      "箱が日本に届いた後、荷主が内容を確認し、韓国返送か廃棄かを決めます。",
    ),
    roles: ["shipper", "admin"],
    keywords: ["처리방안", "한국반송", "폐기", "입고확인", "수량", "処理方針", "韓国返送", "廃棄"],
    body: [
      {
        kind: "p",
        text: t(
          "상태가 입고완료가 되면 화주 차례입니다. 할 일 화면의 입고 확인 대기와 처리방안 미정 카드에서 바로 찾을 수 있습니다.",
          "状態が入庫完了になると荷主の番です。対応待ち画面の「入庫確認待ち」「処理方針未定」カードからすぐ見つけられます。",
        ),
      },
      { kind: "heading", text: t("하는 일", "手順") },
      {
        kind: "steps",
        items: [
          t("반품 상세를 엽니다(목록에서 줄을 누르면 옆판이 열립니다).", "返品詳細を開きます(一覧で行を押すと横のパネルが開きます)。"),
          t(
            "반품 상품 표에서 등록한 반품수량과 쿨트랙JP가 센 검수수량을 견줍니다. 다르면 검수수량이 빨갛게 보입니다.",
            "返品商品の表で、登録した返品数量と日本側が数えた検品数量を比べます。異なる場合は検品数量が赤く表示されます。",
          ),
          t(
            "입고 정보에서 중량·착/선불·착불비·검수 메모를 확인합니다.",
            "入庫情報で重量・着払い/元払い・着払い費用・検品メモを確認します。",
          ),
          t("지금 할 일에서 입고 내용 확인을 누릅니다.", "「今やること」で入庫内容を確認を押します。"),
          t(
            "처리방안 정하기에서 저장을 누릅니다. 한국반송이 미리 골라져 있으니 폐기할 건만 바꿔 고릅니다.",
            "処理方針を決めるで保存を押します。韓国返送があらかじめ選ばれているので、廃棄する件だけ選び直します。",
          ),
        ],
      },      {
        kind: "image",
        src: t("/help/case-detail-ko.png", "/help/case-detail-ja.png"),
        alt: t("반품 상세의 요약 탭", "返品詳細の概要タブ"),
        caption: t(
          "검수 수량이 등록과 다르면 반품 상품 표에서 빨갛게 보입니다(등록 3, 검수 2).",
          "検品数量が登録と異なる場合、返品商品の表に赤く表示されます(登録3、検品2)。",
        ),
      },

      {
        kind: "table",
        head: [t("처리방안", "処理方針"), t("뜻", "意味"), t("그 다음", "その後")],
        rows: [
          [
            t("한국반송", "韓国返送"),
            t("한국으로 되돌려 보낸다.", "韓国へ送り返す。"),
            t("쿨트랙JP가 인보이스 품목을 적고, 선적에 실어 보냅니다.", "日本側がインボイス品目を記入し、船積みして送ります。"),
          ],
          [
            t("폐기", "廃棄"),
            t("일본에서 버린다.", "日本で廃棄する。"),
            t("인보이스 품목이 필요 없습니다. 쿨트랙JP가 폐기 처리로 종결합니다.", "インボイス品目は不要です。日本側が廃棄処理で完了します。"),
          ],
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "처리방안은 서류를 확정하기 전까지 다시 고를 수 있습니다. 수량이 달라 어떻게 할지 정하기 어려우면 항목 확인요청으로 쿨트랙JP에 다시 봐 달라고 할 수 있습니다.",
          "処理方針は書類を確定する前なら選び直せます。数量が合わず判断が難しい場合は、項目の確認依頼で日本側に再確認を求められます。",
        ),
      },
    ],
  },

  {
    slug: "doc-confirm",
    category: "shipper",
    order: 4,
    title: t("서류 확정", "書類の確定"),
    summary: t(
      "쿨트랙JP가 적은 인보이스 품목을 화주가 확인해 확정합니다. 확정해야 선적에 실립니다.",
      "日本側が記入したインボイス品目を荷主が確認して確定します。確定しないと船積みできません。",
    ),
    roles: ["shipper", "admin"],
    keywords: ["서류확정", "확인요청", "서류완료", "반려", "書類確定", "確認依頼"],
    body: [
      {
        kind: "p",
        text: t(
          "실물을 본 쿨트랙JP가 인보이스 품목을 적으면, 주문을 아는 화주가 그 내용을 확인합니다. 확정하면 상태가 서류완료가 되고 그때부터 선적에 실을 수 있습니다.",
          "現物を見た日本側がインボイス品目を記入し、注文を知る荷主がその内容を確認します。確定すると状態が書類完了になり、船積みできるようになります。",
        ),
      },
      {
        kind: "steps",
        items: [
          t("할 일 화면의 서류 확인 대기 카드를 누릅니다.", "対応待ち画面の「書類確認待ち」カードを押します。"),
          t(
            "반품 상세의 인보이스 품목 탭에서 품명·HS코드·원산지·수량·단가를 확인합니다.",
            "返品詳細のインボイス品目タブで品名・HSコード・原産地・数量・単価を確認します。",
          ),
          t("맞으면 지금 할 일에서 서류 확정을 누릅니다.", "問題なければ「今やること」で書類の確定を押します。"),
        ],
      },
      { kind: "heading", text: t("서류 확정이 눌리지 않을 때", "書類の確定が押せないとき") },
      {
        kind: "p",
        text: t(
          "단추 옆에 막힌 이유가 그대로 적힙니다. 아래 중 하나입니다.",
          "ボタンの横に理由が表示されます。次のいずれかです。",
        ),
      },
      {
        kind: "table",
        head: [t("이유", "理由"), t("해야 할 일", "対応")],
        rows: [
          [t("처리방안을 먼저 정하세요.", "先に処理方針を決めてください。"), t("처리방안 정하기에서 한국반송 또는 폐기를 고릅니다.", "処理方針を決めるで韓国返送か廃棄を選びます。")],
          [t("쿨트랙JP의 검수가 아직 끝나지 않았습니다.", "検品がまだ終わっていません。"), t("쿨트랙JP가 반품 상품 줄마다 실제 수량을 세면 풀립니다.", "日本側が返品商品の行ごとに実数量を記入すると解除されます。")],
          [t("인보이스 품목을 먼저 기재하세요.", "先にインボイス品目を記入してください。"), t("한국반송인데 품목이 비어 있습니다. 쿨트랙JP가 적어야 합니다.", "韓国返送なのに品目が空です。日本側の記入が必要です。")],
          [t("항목 확인요청이 걸려 있습니다.", "項目の確認依頼中です。"), t("쿨트랙JP가 품목이나 중량·착선불을 고쳐 저장하면 풀립니다.", "日本側が品目または重量・着払/元払を修正して保存すると解除されます。")],
        ],
      },
      { kind: "heading", text: t("다시 봐 달라고 하기", "再確認を依頼する") },
      {
        kind: "p",
        text: t(
          "품목이 주문과 맞지 않으면 항목 확인요청을 누르고 이유를 적습니다. 서류작성여부가 항목확인요청으로 바뀌고, 쿨트랙JP가 고쳐 저장하면 다시 인보이스 항목기재로 돌아옵니다.",
          "品目が注文と合わない場合は「項目の確認依頼」を押して理由を記入します。書類作成状況が「項目確認依頼」に変わり、日本側が修正して保存すると「インボイス品目記入」に戻ります。",
        ),
      },
    ],
  },

  {
    slug: "kr-arrival",
    category: "shipper",
    order: 5,
    title: t("한국 입고 처리", "韓国入庫の処理"),
    summary: t(
      "배로 온 물건이 한국 지정창고에 들어오면 화주가 찍습니다. 여기서 반품 건이 끝납니다.",
      "船で届いた荷物が韓国の指定倉庫に入ったら荷主が記録します。ここで返品が完了します。",
    ),
    roles: ["shipper", "admin"],
    keywords: ["한국입고", "반송완료", "종결", "도착", "韓国入庫", "返送完了"],
    body: [
      {
        kind: "p",
        text: t(
          "쿨트랙JP가 일본 출항까지 찍으면 선적이 해상 운송중이 되고, 할 일 화면에 한국 입고 대기로 뜹니다. 물건이 한국 창고에 들어온 사실은 한국 쪽만 알기 때문에 이 단계는 화주가 찍습니다.",
          "日本側が日本出航まで記録すると船積みが海上輸送中になり、対応待ち画面に韓国入庫待ちとして表示されます。荷物が韓国の倉庫に入った事実は韓国側しか分からないため、この段階は荷主が記録します。",
        ),
      },
      {
        kind: "steps",
        items: [
          t("왼쪽 메뉴에서 선적을 열고, 해당 선적을 누릅니다.", "左メニューから船積みを開き、対象の船積みを押します。"),
          t("한국 입고 처리 카드에서 한국 입고일을 고릅니다.", "韓国入庫の処理カードで韓国入庫日を選びます。"),
          t("한국 입고 처리를 누릅니다.", "韓国入庫を記録を押します。"),
        ],
      },      {
        kind: "image",
        src: t("/help/shipment-kr-ko.png", "/help/shipment-kr-ja.png"),
        alt: t("선적 상세의 한국 입고 처리 카드", "船積み詳細の韓国入庫カード"),
        caption: t(
          "해상 운송중인 선적에서만 이 카드가 보입니다. 쿨트랙JP 화면에는 나오지 않습니다.",
          "海上輸送中の船積みでのみこのカードが表示されます。日本側の画面には表示されません。",
        ),
      },

      {
        kind: "list",
        items: [
          t("선적 상태가 반송완료가 됩니다.", "船積みの状態が返送完了になります。"),
          t(
            "그 선적에 든 반품 건이 모두 반송완료로 종결되고, 한국반송/폐기일에 입고일이 들어갑니다.",
            "その船積みに含まれる返品がすべて返送完了となり、韓国返送/廃棄日に入庫日が入ります。",
          ),
          t("종결된 건은 더 이상 고칠 수 없습니다.", "完了した件はそれ以上編集できません。"),
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "쿨트랙JP 계정에는 이 단추가 보이지 않습니다. 대신 한국 도착을 기다리는 중입니다 라고만 보입니다.",
          "日本側のアカウントにはこのボタンは表示されず、「韓国到着待ちです」とだけ表示されます。",
        ),
      },
    ],
  },
]
